export type MediaDeviceData<T> = ({ permissionGranted: true } & T) | { permissionGranted: false };

export type MicStreamData = {
    stream?: MediaStream;
    trackDeviceId?: string;
    trackLabel?: string;
};

export type MicsData = {
    devices: MediaDeviceInfo[];
};

export type MediaDeviceResult =
    | { success: true; stream: MediaStream }
    | { success: false; accessDenied: boolean };

export async function watchMicStream(
    deviceId: string | undefined,
    handleChange: (data: MediaDeviceData<MicStreamData>) => void,
    abortSignal: AbortSignal,
) {
    try {
        const getUserMediaStart = performance.now();
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                deviceId: deviceId ? { exact: deviceId } : undefined,
            },
        });
        const getUserMediaEnd = performance.now();
        console.debug(
            `[MediaDevices] getUserMedia took ${(getUserMediaEnd - getUserMediaStart).toFixed(2)}ms`,
        );

        const audioTrack = stream.getAudioTracks()[0];
        audioTrack.addEventListener('ended', () => handleChange({ permissionGranted: true }), {
            signal: abortSignal,
        });

        const getSettingsStart = performance.now();
        const trackDeviceId = audioTrack.getSettings().deviceId;
        const trackLabel = audioTrack.label;
        const getSettingsEnd = performance.now();
        console.debug(
            `[MediaDevices] getSettings took ${(getSettingsEnd - getSettingsStart).toFixed(2)}ms`,
        );

        handleChange({
            stream,
            trackDeviceId,
            trackLabel,
            permissionGranted: true,
        });
    } catch {
        handleChange({
            permissionGranted: false,
        });
    }
}

export async function watchMics(
    handleChange: (data: MediaDeviceData<MicsData>) => void,
    abortSignal: AbortSignal,
) {
    const enumerateStart = performance.now();
    const devices = await navigator.mediaDevices.enumerateDevices();
    const enumerateEnd = performance.now();
    console.debug(
        `[MediaDevices] enumerateDevices took ${(enumerateEnd - enumerateStart).toFixed(2)}ms`,
    );

    navigator.mediaDevices.addEventListener(
        'devicechange',
        () => fetchMics().then((x) => handleChange(x)),
        {
            signal: abortSignal,
        },
    );

    if (devices.some(micPredicate)) {
        handleChange({
            permissionGranted: true,
            devices: devices.filter(micPredicate),
        });
    } else {
        handleChange({
            permissionGranted: false,
        });
    }
}

async function fetchMics(): Promise<MediaDeviceData<MicsData>> {
    const enumerateStart = performance.now();
    const devices = await navigator.mediaDevices.enumerateDevices();
    const enumerateEnd = performance.now();
    console.debug(
        `[MediaDevices] fetchMics enumerateDevices took ${(enumerateEnd - enumerateStart).toFixed(2)}ms`,
    );

    return devices.some(micPredicate)
        ? {
              permissionGranted: true,
              devices: devices.filter(micPredicate),
          }
        : {
              permissionGranted: false,
          };
}

function micPredicate(device: MediaDeviceInfo): boolean {
    return device.kind === 'audioinput' && !!device.label;
}

export function stopMediaStream(stream: MediaStream | undefined): void {
    stream?.getTracks().forEach((x) => x.stop());
}

export async function getDisplayMediaStream(): Promise<MediaDeviceResult> {
    try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
            video: true,
            audio: true,
        });

        return { success: true, stream };
    } catch (error) {
        if (error instanceof Error && error.name === 'NotAllowedError') {
            return { success: false, accessDenied: true };
        }

        console.error('Failed to capture display audio:', error);
        return { success: false, accessDenied: false };
    }
}
